import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const AssetProfilingTab = ({ currentAsset }) => {
  const tableRef = useRef(null);

  const scrollTable = (direction) => {
    if (tableRef.current) {
      const scrollAmount = 200;
      tableRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!currentAsset) return null;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
          <div className="w-[5px] h-6 bg-indigo-500 rounded mr-3"></div>
          Profiling Summary
        </h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-2">Columns Analyzed</p>
            <p className="text-2xl font-bold text-gray-900">{currentAsset.ProfilingInfo?.length || 0}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-2">Rows Profiled</p>
            <p className="text-2xl font-bold text-gray-900">
              {currentAsset.ProfilingInfo?.[0]?.count?.toLocaleString() || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Column Metrics Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800 flex items-center">
            <div className="w-[5px] h-6 bg-indigo-500 rounded mr-3"></div>
            Column Metrics
          </h3>
          <div className="flex items-center gap-1 text-gray-400 text-sm">
            <button 
              onClick={() => scrollTable('left')}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Scroll left"
            >
              <ChevronLeft className=" h-5" />
            </button>
            <span>Scroll</span>
            <button 
              onClick={() => scrollTable('right')}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Scroll right"
            >
              <ChevronRight className=" h-5" />
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" ref={tableRef}>
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Column</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Type</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Count</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Non-Null</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Null %</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Min</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Max</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Mean</th>
                <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Std Dev</th>
              </tr>
            </thead>
            <tbody>
              {currentAsset.ProfilingInfo?.map((metric, index) => (
                <tr key={index} className="border-t border-gray-100">
                  <td className="py-3 px-6 text-sm font-mono text-gray-900">{metric.column_name}</td>
                  <td className="py-3 px-6 text-sm text-gray-700">{metric.type || '-'}</td>
                  <td className="py-3 px-6 text-sm text-gray-700">{metric.count?.toLocaleString() || '-'}</td>
                  <td className="py-3 px-6 text-sm text-gray-700">{metric.countNonNull?.toLocaleString() || '-'}</td>
                  <td className="py-3 px-6">
                    <span className={`text-sm ${
                      metric.nullPercentage > 5 ? 'text-red-600 font-medium' : 'text-gray-700'
                    }`}>
                      {metric.nullPercentage !== undefined ? `${metric.nullPercentage}%` : '-'}
                    </span>
                  </td>
                  <td className="py-3 px-6 text-sm text-gray-700 max-w-[100px] truncate" title={metric.min}>{metric.min || '-'}</td>
                  <td className="py-3 px-6 text-sm text-gray-700 max-w-[100px] truncate" title={metric.max}>{metric.max || '-'}</td>
                  <td className="py-3 px-6 text-sm text-gray-700">{metric.mean !== null ? Number(metric.mean).toFixed(2) : '-'}</td>
                  <td className="py-3 px-6 text-sm text-gray-700">{metric.stddev !== null ? Number(metric.stddev).toFixed(2) : '-'}</td>
                </tr>
              ))}
              {(!currentAsset.ProfilingInfo || currentAsset.ProfilingInfo.length === 0) && (
                <tr>
                  <td colSpan="9" className="py-6 text-center text-gray-500">No profiling data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AssetProfilingTab;
